import React from 'react'

import { Instrument } from '../../../types/shared'
import {
  Button,
  Dialog,
  DialogTrigger,
  Dropdown,
  DropdownItem,
  Icon,
  Spinner,
  Search as WebsiteSearchComponent,
  useDialogState,
  useDropdownState,
} from '../../components'
import { useAsyncModule } from '../../utils/async-modules-utils'
import { createAlertAsyncModule, saveToPortfolioAsyncModule } from '../../website-shared'

interface SearchProps {
  ticker: string
  instrument: Instrument
  onTickerSelected: (ticker: string) => void
  chartIndex: number
}

export default function Search({ ticker, instrument, onTickerSelected, chartIndex }: SearchProps) {
  const dropdownState = useDropdownState({ placement: 'bottom-end' })
  const saveToPortfolioDialogState = useDialogState()
  const createAlertDialogState = useDialogState()
  const [saveToPortfolioModule, { isLoading: isLoadingSaveToPortfolio }] = useAsyncModule({
    ...saveToPortfolioAsyncModule,
    shouldLoadModule: saveToPortfolioDialogState.visible,
    onError: () => saveToPortfolioDialogState.hide(),
  })
  const [createAlertModule, { isLoading: isLoadingCreateAlert }] = useAsyncModule({
    ...createAlertAsyncModule,
    shouldLoadModule: createAlertDialogState.visible,
    onError: () => createAlertDialogState.hide(),
  })
  const SaveToPortfolio = saveToPortfolioModule?.SaveToPortfolio
  const CreateAlert = createAlertModule?.CreateAlert

  const detailLink = React.useMemo(() => {
    const formattedTicker = ticker.replace('@', '')

    switch (instrument) {
      case Instrument.Stock:
        return `/quote.ashx?t=${formattedTicker}`
      case Instrument.Futures:
        return `/futures_charts.ashx?t=${formattedTicker}`
      case Instrument.Forex:
        return `/forex_charts.ashx?t=${formattedTicker}`
      case Instrument.Crypto:
        return `/crypto_charts.ashx?t=${formattedTicker}`
    }
  }, [ticker, instrument])

  return (
    <div className="search flex w-32 flex-none items-center">
      <WebsiteSearchComponent
        chartIndex={chartIndex}
        placeholder={ticker}
        size="medium"
        inputClass="pr-7 placeholder:text-gray-900 focus:placeholder:text-gray-500 dark:placeholder:text-white dark:focus:placeholder:text-gray-400"
        isSearchIcon={false}
        isSearchItemLink={false}
        isShowAllVisible={false}
        onTickerSelected={onTickerSelected}
        rightContent={
          <Dropdown
            state={dropdownState}
            aria-label="More options"
            label={
              <Button
                type="button"
                size="none"
                className="h-full w-7 text-gray-400 hover:text-gray-500"
                theme="transparent"
                appearance="square"
                rightContent={<Icon width={20} name="more" />}
              />
            }
          >
            <DropdownItem
              as="a"
              target="_blank"
              href={detailLink}
              onClick={() => {
                window.gtag?.('event', 'click', { event_category: 'open-stock-detail' })
              }}
            >
              {instrument === Instrument.Stock ? 'Open Stock Detail' : 'Open Detail'}
            </DropdownItem>
            {instrument === Instrument.Stock && (
              <DropdownItem
                as={DialogTrigger}
                state={saveToPortfolioDialogState}
                onClick={() => {
                  window.gtag?.('event', 'click', { event_category: 'save-to-portfolio' })
                }}
              >
                Save to Portfolio
              </DropdownItem>
            )}
            {instrument === Instrument.Stock && (
              <DropdownItem
                as={DialogTrigger}
                state={createAlertDialogState}
                onClick={() => {
                  window.gtag?.('event', 'click', { event_category: 'create-alert' })
                }}
              >
                Create Alert
              </DropdownItem>
            )}
          </Dropdown>
        }
      />
      <Dialog state={saveToPortfolioDialogState} aria-label="Save to Portfolio" className="w-110">
        {isLoadingSaveToPortfolio && (
          <div className="flex h-56 items-center justify-center">
            <Spinner />
          </div>
        )}
        {SaveToPortfolio && <SaveToPortfolio ticker={ticker} onClose={saveToPortfolioDialogState.hide} />}
      </Dialog>
      <Dialog state={createAlertDialogState} aria-label="Create Alert" className="w-[366px]">
        {isLoadingCreateAlert && (
          <div className="flex h-96 items-center justify-center">
            <Spinner />
          </div>
        )}
        {CreateAlert && <CreateAlert ticker={ticker} onClose={createAlertDialogState.hide} />}
      </Dialog>
    </div>
  )
}
